import { useEffect, useState } from "react";
import ConnectButton from "@/components/ConnectButton";

import { getRegisterStatusByFidApi, registerApi } from "@/api/whitelist";
import { useAccount } from "wagmi";
import { isAxiosError } from "axios";
import { UserContext } from "@/types/user";
import sdk from "@farcaster/miniapp-sdk";

import { WHITELIST_SHARE_TEXT } from "@/constants/share";
import { MINIAPP_FID } from "@/constants/fid";
import { MINIAPP_SHARE_URL } from "@/constants/url";
import { DEMO_ADDRESS } from "@/constants/demo";

import { motion, AnimatePresence } from "motion/react";
import style from "./style.module.scss";
import toast from "react-hot-toast";

type Props = {
  user: UserContext | undefined;
};

const WhiteListView = ({ user }: Props) => {
  const { isConnected, address } = useAccount();
  const connected = import.meta.env.PROD ? isConnected : true;
  const connectedAddress =
    import.meta.env.PROD && address ? address : DEMO_ADDRESS;

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasShared, setHasShared] = useState(false);
  const [hasFollowed, setHasFollowed] = useState(false);
  const [registered, setRegistered] = useState(false);

  const closeModal = () => setShowSuccessModal(false);

  const handleShare = async () => {
    const result = await sdk.actions.composeCast({
      text: WHITELIST_SHARE_TEXT,
      embeds: [MINIAPP_SHARE_URL],
    });
    if (result) setHasShared(true);
  };

  const handleViewProfile = async () => {
    await sdk.actions.viewProfile({
      fid: MINIAPP_FID,
    });
    setHasFollowed(true);
  };

  const handleRegister = async () => {
    if (import.meta.env.PROD) {
      if (!user || !address) return;
    } else if (!user) return;

    setLoading(true);
    try {
      // Show loading state
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const { fid } = user;
      // Call API register
      await registerApi(fid, connectedAddress);
      setRegistered(true);
      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      // handle 400 error
      if (isAxiosError(err) && err.response?.status === 400) {
        toast.error("This wallet address is already registered.", {
          duration: 3000,
        });
        return;
      }
      console.error("Something went wrong during registration:", err);
      toast.error("Something went wrong during registration. Try again", {
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!user) return;
      try {
        const data = await getRegisterStatusByFidApi(user.fid);
        setRegistered(data.registered);
      } catch (err) {
        console.error("Failed to load user data:", err);
        toast.error("Failed to load user data. Please refresh and try again.", {
          duration: 3000,
        });
      }
    };
    fetchUser();
  }, [user]);

  if (!connected)
    return (
      <div className={style.whitelistWrapper}>
        <ConnectButton />
        <div className={style.whitelistBg}>
          <img src="/images/whitelist-bg.png" alt="" />
        </div>
      </div>
    );

  const renderButton = () => {
    const steps = [
      {
        condition: registered,
        label: "Registered",
        disabled: true,
      },
      {
        condition: !hasStarted,
        label: "Start",
        onClick: () => setHasStarted(true),
      },
      {
        condition: !hasShared,
        label: "Share",
        onClick: handleShare,
      },
      {
        condition: !hasFollowed,
        label: "Follow",
        onClick: handleViewProfile,
      },
    ];

    for (const step of steps) {
      if (step.condition) {
        return (
          <button
            className={`button-flat ${style.whitelistButton}`}
            onClick={step.onClick}
            disabled={step.disabled}
          >
            {step.label}
          </button>
        );
      }
    }

    return (
      <button
        className={`button-flat ${style.whitelistButton}`}
        onClick={handleRegister}
        disabled={loading}
      >
        {loading ? "Processing..." : "Register"}
      </button>
    );
  };

  return (
    <div className={style.whitelistWrapper}>
      <div className={style.whitelistBg}>
        <img src="/images/whitelist-bg.png" alt="" />
      </div>
      {renderButton()}

      <AnimatePresence>
        {showSuccessModal && (
          <>
            <motion.div
              className={style.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            />

            <motion.div
              className={style.modal}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.25 }}
            >
              <h2>Congratulations!</h2>
              <p>
                Your wallet has been added to the whitelist for the free mint
              </p>

              <button
                className="button-flat"
                style={{ borderRadius: "4px", minWidth: "100px" }}
                onClick={closeModal}
              >
                Close
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhiteListView;
